<template>
  <div class="site__centered">

    <Breadcrumbs
      v-if="isParentRole"
      :items="[
        { label: 'My Account', name: 'home', type: 'route' },
        { label: programName, id: programId, type: 'program' },
        { label: `${camperStudent('singular')} Selection`, name: '', type: 'route' }
      ]"
    />

    <div class="application form">
      <h2 class="application__title">Application Form</h2>
      <h2 class="application__program">Program name: <span>{{ programName }}</span></h2>
      <h4 class="application__subtitle">{{ selectChildSubtitleText }}</h4>
      <div
        v-if="childrenList.length > 0"
      >
        <ChildItem 
          v-for="(child, index) in childrenList"
          :key="index"
          :child="child"
          :disabled="child.isDisabled"
          :disable-reason="child.disabledReason"
          @click.native="handleChildClick(child)"
        />
      </div>
      <button 
        class="btn btn--color-dark form__button capitalize"
        @click="addNewChild"
      >{{ addChildButtonText }}</button>
    </div>
    <AddNewChildModal 
      :is-show-modal="isShowModal"
      :default-gender="defaultProgramGender"
      :is-enable-select-gender="enableSelectGender"
      :family-name="parent.last_name"
      :family-id="parent.family_id"
      @closeModal="closeModal"
      @updateChildrenList="setChildrenList"
    />

    <AddChildMissingValueModal
      v-if="selectedChild"
      :is-show-modal="isShowSetMissingDataModal"
      :user="selectedChild"
      @closeModal="closeSetMissingValueModal"
      @updateChildrenList="setChildrenList"
    />

    <ReviewChildProfileModal
      v-if="selectedChild"
      :is-show-modal="isShowReviewModal"
      :user="selectedChild"
      @closeModal="closeReviewModal"
      @reviewed="onChildReviewed"
    />

  </div>
</template>

<script>

  import { scrollToTop } from '@/helpers/scrollToTop'
  import ChildItem from '@/views/Children/ChildItem.vue'
  import AddNewChildModal from '@/components/site/add-new-child-modal/AddNewChildModal.vue'
  import store from '@/store'
  import { getUserData } from '@/auth/utils'
  import { prepareProgramRegistrationInfo } from '@core/mixins/prepareProgramRegistrationInfo'
  import { mapGetters } from 'vuex'
  import AddChildMissingValueModal from '@/components/site/AddChildMissingValueModal.vue'
  import ReviewChildProfileModal from '@/components/site/add-new-child-modal/ReviewChildProfileModal.vue'
  import Breadcrumbs from '@/components/site/breadcrumb/Breadcrumbs.vue'
  import { instanceSessionCamperNames } from '@core/mixins/instanceSessionCamperNames'
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

  export default {
    name: 'ApplicationSelectChild',
    components: {
      Breadcrumbs,
      AddChildMissingValueModal,
      ReviewChildProfileModal,
      ChildItem,
      AddNewChildModal,
    },
    mixins: [ prepareProgramRegistrationInfo, instanceSessionCamperNames ],
    data() {
      return {
        parent: getUserData(),
        isParentRole: getUserData().role === 'parent',
        isShowModal: false,
        isShowSetMissingDataModal: false,
        isShowReviewModal: false,
        selectedChild: null,
        programId: null,
        programSlug: null,
        programName: '',
        defaultProgramGender: null,
        enableSelectGender: false,
        disableReason: '',
        isLoading: false,
      }
    },
    computed: {
      ...mapGetters({
        authSettings: 'authSettings/getAuthSettings',
        childrenList: 'children/getChildrenList',
        program: 'programs/getProgram',
      }),
      selectChildSubtitleText() {
        return this.getCustomText(
          this.authSettings,
          'application_select_child_subtitle',
          'Select {camperStudent} to register for',
          { camperStudent: this.camperStudent('singular') },
        )
      },
      addChildButtonText() {
        return this.getCustomText(
          this.authSettings,
          'application_select_child_add_button',
          'Add A {camperStudent}',
          { camperStudent: this.camperStudent('singular') },
        )
      },
    },
    watch: {
      async $route() {
        if (this.$route.params.id !== this.programId) {
          this.programId = this.$router.currentRoute.params.id
          await this.setProgramAndChildrenData()
        }
      },
    },
    async created() {
      if (this.$router.currentRoute.params.slug) {
          this.programSlug = this.$router.currentRoute.params.slug
      }

      if (this.$router.currentRoute.params.id) {
        this.programId = this.$router.currentRoute.params.id
      }

      if (!this.programId && !this.programSlug) {
        await this.$router.push({ name: 'error-404' })
      }

      await this.setProgramAndChildrenData()
    },
    methods: {
      async setProgramAndChildrenData() {
        try {
          store.commit('SET_LOADING_STATUS', true)

          let response

          if (this.programId) {
            response = await store.dispatch('programs/fetchProgramById', this.programId)
          } else if (this.programSlug) {
            response = await store.dispatch('programs/fetchProgramBySlug', this.programSlug)
          }

          if (response) {
            this.programName = response.name
            if (!this.programId) {
              this.programId = response.id
            }
            this.defaultProgramGender = this.getDefaultGenderForProgram(response.gender, this.authSettings)

            store.commit('children/SET_SELECTED_PROGRAM_AVAILABILITY_SETTINGS', response)
            this.enableSelectGender = response.gender === 'Both'
          }

          await this.setChildrenList()
        } catch (error) {
          if (error.response && error.response.status === 404) {
            this.$router.push('error-404')
            return
          }
          console.error('Error loading program or children data:', error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error loading data',
              text: 'Failed to load program or children information. Please try again.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        } finally {
          store.commit('SET_LOADING_STATUS', false)
        }
      },
      handleChildClick(child) {
        if (child.ageIsEmpty || child.dynamicFieldIsEmpty) {
          this.selectedChild = child
          this.isShowSetMissingDataModal = true
        } else if (child.needsProfileReview) {
          this.selectedChild = child
          this.isShowReviewModal = true
        } else {
          this.$store.dispatch('children/goToApplicationPage', {
            child: child,
            programId: this.programId,
          })
        }
      },
      addNewChild() {
        scrollToTop()
        this.isShowModal = true
      },
      closeModal() {
        this.isShowModal = false
      },
      closeSetMissingValueModal() {
        this.isShowSetMissingDataModal = false
        this.selectedChild = null
      },
      closeReviewModal() {
        this.isShowReviewModal = false
        this.selectedChild = null
      },
      onChildReviewed() {
        const child = this.selectedChild
        this.closeReviewModal()

        // Invalidate the cached children list so the beforeRouteEnter guard in
        // ApplicationSteps fetches fresh data (with the updated profile_reviewed_at)
        // instead of using the stale cached version.
        this.$store.commit('children/INVALIDATE_CHILDREN_LIST')

        // child.id and applicationsByProgram are unchanged by a profile review,
        // so we can navigate immediately without re-fetching.
        this.$store.dispatch('children/goToApplicationPage', {
          child,
          programId: this.programId,
        })
      },
      async setChildrenList() {
        this.isLoading = true
        this.$emit('loading', true)

        try {
          await this.$store.dispatch('children/fetchChildrenList')
          await this.$store.dispatch('children/updateChildrenListWithProgramAccess')
        } catch (error) {
          console.error('Error fetching children list:', error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error loading children',
              text: 'Failed to load the children list. Please refresh the page.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        } finally {
          this.$emit('loading', false)
          this.isLoading = false
        }
      },
    },
  }
</script>

<style scoped>
</style>